import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { Alert, Box, Button, Col, Icon, Placeholder, Row, Tabs, Text } from '@/client/components'
import ProgressBar from '@/client/components/ProgressBar/ProgressBar'
import { CartContext } from '@/client/context/cart'
import {
  useGetCalculatorInfos,
  useGetCartItems,
  useGetPrices,
  useMutateAddCartItem,
  useMutatePrices,
  useMutateReplaceCartItem,
  useMutateUpdateCartItem,
  useRouterPush,
  useWindowSize
} from '@/client/hooks'
import { usePackageInfos } from '@/client/hooks/generalPackages'
import { IApiKeysJobType } from '@/client/types'
import { ICalculatorTemplatePositiveList } from '@/client/types/OrderTemplate'
import { applyPreferredCurrency, format, getCurrencyCodeById, getEffectiveCurrency, ROUTER_PATH } from '@/client/utils'
import { useI18n } from '@/locales'
import { CalculatorStep } from './CalculatorStep'
import { getOptionsField, updateInitialSelection } from './helper'
import { Options } from './Options'
import { Result } from './Result'
import { ResultFooter } from './ResultFooter'
import { ServiceResult } from './ServiceResult'
import * as S from './styles'
import { Summary } from './Summary'
import { ARTWORKSERVICE_SELECTION, CalculatorProps, calculatorTemplateProps, PROOF_TYPE, Steps } from './types'

/**
 * Calculator component. The heart of the application. :-)
 *
 * @param serviceType initialize with an artwork / proofing service type
 * @param isArtworkService indicates if the calculator is used for a standalone artwork service
 * @param showAsWizard true is the standard, if false we show all steps in a single step
 * @param template if given the calculator will be initialized with some selection coming from a template, also controls visibility and editability
 * @param compactView if set to true, the calculator will be displayed in a compact view (a more understated design, with simple drop-downs)
 * @param onContinueShopping Callback that is called when the user clicks on "Continue Shopping"
 * @constructor
 */
export const Calculator: FC<CalculatorProps> = ({
  serviceType,
  isArtworkService = false,
  showAsWizard = true,
  template,
  compactView = false,
  onContinueShopping
}) => {
  const calculatorRef = useRef<HTMLDivElement>(null)
  const settingsRef = useRef<HTMLDivElement>(null)
  const [step, setStep] = useState<Steps>(Steps.SIZE_AND_FORMAT)
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false)
  const [resetState, setResetState] = useState<boolean>(false)
  const windowSize = useWindowSize()
  const isXlBreakpoint = windowSize?.width > 1040
  const { locale, defaultLocale, routerPush } = useRouterPush()
  const { data } = useGetPrices(locale)
  const { data: calculatorInfos } = useGetCalculatorInfos(locale || defaultLocale)
  const { data: generalPackages } = usePackageInfos(locale || defaultLocale)
  const { updateSelection, selection } = useContext(CartContext)
  const { data: cartData } = useGetCartItems()
  const currentCurrency = getCurrencyCodeById(selection?.currency_id as number) || getEffectiveCurrency(cartData)
  const { isLoading, mutate } = useMutatePrices(locale)
  const openCalculatorRef = useRef<HTMLDivElement>(null)
  const { mutate: addCartItem } = useMutateAddCartItem(locale)
  const { mutate: updateCartItem } = useMutateUpdateCartItem()
  const { mutate: replaceCartItem } = useMutateReplaceCartItem(locale)
  const { mutate: updatePrices } = useMutatePrices(locale)
  const t = useI18n()

  const calculatorTabs = () => {
    const steps = Object.entries(calculatorInfos?.steps || []).map((step, index) => ({
      id: step[0],
      key: index + 1,
      name: String(step[1].title)
    }))

    const getFilteredSteps = (desiredIds) =>
      steps
        .filter((step) => desiredIds.includes(step.id))
        .map((step, index) => {
          if (step.id !== 'step_7' && step.id !== 'step_8') {
            return {
              ...step,
              key: index + 1
            }
          } else {
            return step
          }
        })

    switch (serviceType) {
      case 'basic':
        const basicDesiredIds = ['step_1', 'step_2', 'step_3', 'step_7', 'step_8']
        return getFilteredSteps(basicDesiredIds)
      case 'premium':
        const premiumDesiredIds = ['step_1', 'step_2', 'step_3', 'step_4', 'step_6', 'step_7', 'step_8']
        return getFilteredSteps(premiumDesiredIds)
      case 'smart':
        const smartDesiredIds = ['step_1', 'step_2', 'step_3', 'step_7', 'step_8']
        return getFilteredSteps(smartDesiredIds)
      case 'pro':
        const proDesiredIds = ['step_1', 'step_2', 'step_3', 'step_4', 'step_7', 'step_8']
        return getFilteredSteps(proDesiredIds)
      default:
        return steps
    }
  }

  const handleServiceTypeChanges = (type, selection) => {
    switch (type) {
      case 'basic':
        return {
          ...selection,
          proof: PROOF_TYPE.BASIC_PROOF,
          quantity: 5
        }

      case 'premium':
        return {
          ...selection,
          proof: PROOF_TYPE.PREMIUM_PROOF,
          quantity: 30 //REL-811 Update premium quantity to 30
        }

      case 'smart':
        return {
          ...selection,
          artwork_service: ARTWORKSERVICE_SELECTION.SMART_ARTWORK_SERVICE, //smart artwork service id from rel_attributes table
          job_type: IApiKeysJobType.STANDALONE_ARTWORK_SERVICE, //stand alone smart artwork service
          quantity: 10
        }

      case 'pro':
        return {
          ...selection,
          artwork_service: ARTWORKSERVICE_SELECTION.PRO_ARTWORK_SERVICE, //pro artwork service id from rel_attributes table
          job_type: IApiKeysJobType.STANDALONE_ARTWORK_SERVICE, //stand alone pro artwork service
          quantity: 10
        }

      default:
        return selection
    }
  }

  useEffect(() => {
    if (!data?.initialSelection || !serviceType) return

    // persist the calculator selection when
    // 1. editing cartitems
    // 2. redirected from a data error/issue email
    if (selection && serviceType === selection.serviceType && (selection._id || selection.related_job_id)) {
      mutate(selection)
      return
    }

    let newSelection = updateInitialSelection(data.initialSelection)
    newSelection = applyPreferredCurrency(newSelection, cartData)

    if (isArtworkService) {
      newSelection.recipient = '0'
    }

    newSelection = handleServiceTypeChanges(serviceType, newSelection)

    updateSelection(newSelection)
    mutate(newSelection)
    // Depend on the cart's currency-relevant primitives, not the cartData object
    // itself - useGetCartItems returns a brand-new object on every render, so
    // using it directly here would re-run this effect (and re-hit the price
    // endpoint via mutate) on every render, forever.
  }, [
    data?.initialSelection,
    serviceType,
    isArtworkService,
    template,
    cartData?.cart_items?.length,
    cartData?.currency_id
  ])

  // Part of REL-776, preset values from a template and trigger price calculation
  useEffect(() => {
    if (!template) return

    // Already applied this template to the current selection - avoid re-triggering in a loop
    if (selection?.order_template_id === template.id) return

    // If there's no selection yet, seed it with the standard initial values first
    const baseSelection = selection || (data?.initialSelection && updateInitialSelection(data.initialSelection))
    if (!baseSelection) return

    // Ensure that values that are numbers are set as numbers like sorts, quantity etc.
    const normalizeTemplateValue = (
      value: string | number | boolean | number[] | ICalculatorTemplatePositiveList | undefined
    ) => {
      if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
        return Number(value)
      }

      return value
    }

    const newSelection = { ...baseSelection }

    calculatorTemplateProps.forEach((key) => {
      if (template[key]) {
        if (key == 'double_hit') {
          // All props that expect an array of values which is currently just double_hit
          newSelection[key] = template[key]?.value_preset_list
        } else if (key !== null) {
          const templateValue = template[key]?.value_preset_list?.at(0) || template[key]?.value
          newSelection[key] = normalizeTemplateValue(templateValue)
        }
      }
    })

    // We have to add the order_template_id to be able to get customer specific prices for this order template
    newSelection.order_template_id = template.id

    updateSelection(newSelection)
    mutate(newSelection) // Trigger price calculation at the start
  }, [template, selection, data?.initialSelection])

  const resetAll = useCallback(() => {
    if (!data?.initialSelection) return

    let newSelection = updateInitialSelection(data.initialSelection)
    newSelection = applyPreferredCurrency(newSelection, cartData)

    newSelection = {
      ...newSelection,
      order_name: '',
      artwork_service_project_name: '',
      first_name: '',
      surname: '',
      email: '',
      recipient: '',
      artwork_service_description: ''
    }

    //persist the jobType or proofType while reseting
    newSelection = handleServiceTypeChanges(serviceType, newSelection)

    updateSelection(newSelection)
    mutate(newSelection)

    setResetState(true)
  }, [data?.initialSelection, serviceType, cartData?.cart_items?.length, cartData?.currency_id])

  // Default Calculator
  const handleSetStep = useCallback(
    (step: Steps) => {
      setStep(step)
      if (calculatorRef && calculatorRef.current) {
        // Disabling smooth scroll temporarily to the calculator.
        // Uncomment to restore smooth scrolling.
        // requestAnimationFrame(scrollToCalculator)
        calculatorRef.current.scrollIntoView()
      }
    },
    [calculatorRef]
  )

  const handleBackStep = useCallback(() => {
    if (step > Steps.SIZE_AND_FORMAT) {
      switch (true) {
        case serviceType === 'premium' && step === 7:
          handleSetStep(step - 2)
          break
        case serviceType === 'basic' && step === 7:
          handleSetStep(step - 4)
          break
        case serviceType === 'smart' && step === 7:
          handleSetStep(step - 4)
          break
        case serviceType === 'pro' && step === 7:
          handleSetStep(step - 3)
          break
        default:
          handleSetStep(step - 1)
          break
      }
    }
  }, [setStep, step])

  const handleStep = useCallback(async () => {
    if (step !== Steps.SUMMARY) {
      switch (true) {
        case serviceType === 'premium' && step === Steps.CUT: // 5:
          handleSetStep(Steps.OPTIONS) // step + 2)
          break
        case serviceType === 'basic' && step === Steps.COLOUR_AND_FINISHES: // 3:
          handleSetStep(Steps.OPTIONS) // step + 4)
          break
        case serviceType === 'smart' && step === Steps.COLOUR_AND_FINISHES: // 3:
          handleSetStep(Steps.OPTIONS) // step + 4)
          break
        case serviceType === 'pro' && step === Steps.REFINEMENT: // 4:
          handleSetStep(Steps.OPTIONS) // step + 3)
          break
        default:
          handleSetStep(step + 1)
          break
      }
    }

    // REL-776: We simulate as if we were on the last page. As React does not synchronously update the step object,
    // we have to handle this extra
    if (!showAsWizard) {
      handleSetStep(Steps.SUMMARY)
    }

    if (step === Steps.SUMMARY || !showAsWizard) {
      //add job description/order name to proofing service if not provided
      let finalSelection = selection
      if (
        (serviceType === 'basic' || serviceType === 'premium') &&
        finalSelection.proof &&
        !finalSelection.order_name
      ) {
        finalSelection = {
          ...finalSelection,
          order_name: `${t('PROOFING_SERVICE')} ${serviceType} ${selection.width} x ${selection.height} ${
            defaultLocale === 'us' ? 'in' : 'mm'
          }`
        }
        updateSelection(finalSelection)
      }
      //omit out details related to transparency
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tpy_cta, tpy_lang, tpy_type, tpy_layout, ...rest } = finalSelection

      const newSelection = applyPreferredCurrency(updateInitialSelection(data?.initialSelection), cartData)
      updateSelection(newSelection)
      updatePrices(newSelection)

      if (selection._id) {
        replaceCartItem({
          cartItemId: selection._id,
          payload: { ...rest }
        })
        routerPush(ROUTER_PATH.CART[defaultLocale])
      } else {
        addCartItem(rest)
        setIsAlertOpen(true)
        setStep(Steps.SIZE_AND_FORMAT)
      }
    }
  }, [
    step,
    setStep,
    updatePrices,
    routerPush,
    addCartItem,
    updateCartItem,
    data?.initialSelection,
    updateSelection,
    selection,
    showAsWizard,
    cartData?.cart_items?.length,
    cartData?.currency_id
  ])

  const options = useMemo(
    () => [...(data?.calculatorFields || []), ...(data?.optionFields || [])],
    [data?.calculatorFields, data?.optionFields]
  )

  const getProgress = useCallback(
    (step: number) => (calculatorTabs().length === 0 ? 15 : step * (100 / calculatorTabs().length)),
    [step]
  )

  //check if a paper is selected
  const paperSelected = useMemo((): boolean => {
    if (!selection) return false
    return !!getOptionsField(data?.calculatorFields, 'paper').find((option) => option.id === (selection['paper'] ?? 0))
  }, [selection, data?.calculatorFields])

  //hotfoil or relief embossing hint
  const finishingType =
    options.length > 0 &&
    selection &&
    options?.find((item) => item.key === 'finishing')?.options?.find((item) => item.id === selection.finishing)?.key

  const finishingTypeHint =
    finishingType === 'foil' ? 'hotfoil_hint' : finishingType === 'relief_embossing' ? 'relief_embossing_hint' : null

  const shapeName =
    options.length > 0 &&
    selection &&
    options?.find((item) => item.key === 'label_shape')?.options?.find((item) => item.id === selection.label_shape)?.key

  return (
    <>
      <div ref={settingsRef}>
        <Row ref={calculatorRef} className='tour-calculator' name='tour-calculator'>
          {!calculatorInfos && (
            <>
              <Col xs={12} xl={8}>
                <Placeholder kind='card' height='30rem' />
              </Col>

              <Col xs={12} xl={4}>
                <Placeholder kind='card' height='30rem' />
              </Col>
            </>
          )}
          {!!data && calculatorInfos && selection && (
            <>
              <Col xs={12} xl={8}>
                <Box shadow>
                  {!compactView && (
                    <>
                      <Tabs
                        stepActive={step}
                        endingStepNumber={Steps.SUMMARY}
                        steps={calculatorTabs()}
                        price={format.currency(data?.price.total, data?.jobProps?.currency || currentCurrency, locale)}
                        resume={calculatorInfos.resume.footer_message_mobile}
                        action={setStep}
                        kind={'withIcon'}
                        serviceType={serviceType}
                        isDisabled={
                          isLoading ||
                          !(data?.price?.priceTable?.length > 0) ||
                          (step === Steps.OPTIONS &&
                            (isArtworkService
                              ? !selection.artwork_service_description || !selection.artwork_service_project_name
                              : selection.artwork_service === 0))
                        }
                      />

                      <ProgressBar progress={getProgress(step)} />
                    </>
                  )}

                  <>
                    <S.Body ref={openCalculatorRef}>
                      <>
                        {data && showAsWizard && (
                          <CalculatorStep
                            fields={data?.calculatorFields}
                            texts={calculatorInfos.steps.step_7}
                            step={step}
                            randomText={calculatorInfos.random_text}
                            initialSelection={data?.initialSelection}
                            reset={resetState}
                            setResetFalse={() => setResetState(false)}
                            serviceType={serviceType}
                            artworkServiceText={calculatorInfos.artwork_service_model}
                          />
                        )}
                        {!showAsWizard && (
                          <>
                            <CalculatorStep
                              fields={data?.calculatorFields}
                              texts={calculatorInfos.steps.step_7}
                              step={Steps.SIZE_AND_FORMAT}
                              randomText={calculatorInfos.random_text}
                              initialSelection={data?.initialSelection}
                              reset={resetState}
                              setResetFalse={() => setResetState(false)}
                              serviceType={serviceType}
                              artworkServiceText={calculatorInfos.artwork_service_model}
                              template={template}
                              compactView={compactView}
                            />
                            <CalculatorStep
                              fields={data?.calculatorFields}
                              texts={calculatorInfos.steps.step_7}
                              step={Steps.MATERIAL_AND_ADHESIVE}
                              randomText={calculatorInfos.random_text}
                              initialSelection={data?.initialSelection}
                              reset={resetState}
                              setResetFalse={() => setResetState(false)}
                              serviceType={serviceType}
                              artworkServiceText={calculatorInfos.artwork_service_model}
                              template={template}
                              compactView={compactView}
                            />
                            <CalculatorStep
                              fields={data?.calculatorFields}
                              texts={calculatorInfos.steps.step_7}
                              step={Steps.COLOUR_AND_FINISHES}
                              randomText={calculatorInfos.random_text}
                              initialSelection={data?.initialSelection}
                              reset={resetState}
                              setResetFalse={() => setResetState(false)}
                              serviceType={serviceType}
                              artworkServiceText={calculatorInfos.artwork_service_model}
                              template={template}
                              compactView={compactView}
                            />
                            <CalculatorStep
                              fields={data?.calculatorFields}
                              texts={calculatorInfos.steps.step_7}
                              step={Steps.REFINEMENT}
                              randomText={calculatorInfos.random_text}
                              initialSelection={data?.initialSelection}
                              reset={resetState}
                              setResetFalse={() => setResetState(false)}
                              serviceType={serviceType}
                              artworkServiceText={calculatorInfos.artwork_service_model}
                              template={template}
                              compactView={compactView}
                            />
                            <CalculatorStep
                              fields={data?.calculatorFields}
                              texts={calculatorInfos.steps.step_7}
                              step={Steps.CUT}
                              randomText={calculatorInfos.random_text}
                              initialSelection={data?.initialSelection}
                              reset={resetState}
                              setResetFalse={() => setResetState(false)}
                              serviceType={serviceType}
                              artworkServiceText={calculatorInfos.artwork_service_model}
                              template={template}
                              compactView={compactView}
                            />
                            <CalculatorStep
                              fields={data?.calculatorFields}
                              texts={calculatorInfos.steps.step_7}
                              step={Steps.MECHINE_PROCESSING}
                              randomText={calculatorInfos.random_text}
                              initialSelection={data?.initialSelection}
                              reset={resetState}
                              setResetFalse={() => setResetState(false)}
                              serviceType={serviceType}
                              artworkServiceText={calculatorInfos.artwork_service_model}
                              template={template}
                              compactView={compactView}
                            />
                            <CalculatorStep
                              fields={data?.calculatorFields}
                              texts={calculatorInfos.steps.step_7}
                              step={Steps.OPTIONS}
                              randomText={calculatorInfos.random_text}
                              initialSelection={data?.initialSelection}
                              reset={resetState}
                              setResetFalse={() => setResetState(false)}
                              serviceType={serviceType}
                              artworkServiceText={calculatorInfos.artwork_service_model}
                              template={template}
                              compactView={compactView}
                            />
                          </>
                        )}
                        {(step === Steps.OPTIONS || !showAsWizard) && data && generalPackages && (
                          <S.OptionWrapperBase>
                            <S.OptionsWrapper>
                              <>
                                <Options
                                  fields={data?.optionFields}
                                  texts={calculatorInfos.steps.step_7}
                                  wpText={generalPackages}
                                  isArtworkService={isArtworkService}
                                  artworkServiceText={calculatorInfos.artwork_service_model}
                                  initialSelection={data?.initialSelection}
                                  template={template}
                                  compactView={compactView}
                                />
                              </>
                            </S.OptionsWrapper>
                          </S.OptionWrapperBase>
                        )}
                        {(step === Steps.SUMMARY || !showAsWizard) && data && (
                          <Summary
                            calculatorFields={data?.calculatorFields}
                            optionFields={data?.optionFields}
                            setStep={handleSetStep}
                            texts={calculatorInfos.steps.step_8}
                            randomText={calculatorInfos.random_text}
                            isArtworkService={isArtworkService}
                            production_time={data.price}
                            template={template}
                            compactView={compactView}
                            serviceType={serviceType}
                          />
                        )}
                        <S.BorderDivider>
                          <Row style={{ paddingTop: '2rem' }}>
                            <Col xs={12} xl={3}>
                              <Button kind='weak' isPill={true} onMouseDown={resetAll} id={'calc_b_reload'}>
                                <Icon name='reload' />
                                {calculatorInfos?.random_text?.refresh_button}
                              </Button>
                            </Col>
                            <Col xs={12} xl={9}>
                              <S.GeneralWrap>
                                {/* default calculator bottom buttons */}
                                <Button
                                  kind='weak'
                                  isPill={true}
                                  onClick={handleBackStep}
                                  disabled={isLoading || step === Steps.SIZE_AND_FORMAT}
                                  style={{ visibility: showAsWizard ? 'visible' : 'hidden' }}
                                  id={step !== 1 ? 'calc_b_prev' + `${step - 1}` : 'calc_b_prev'}
                                >
                                  <Icon name='go-back' /> {calculatorInfos?.random_text?.backward_button}
                                </Button>
                                {step !== Steps.SUMMARY && showAsWizard && (
                                  <Button
                                    kind='warning'
                                    isPill={true}
                                    onClick={handleStep}
                                    disabled={
                                      isLoading ||
                                      !(data?.price?.priceTable?.length > 0) ||
                                      (step === Steps.OPTIONS &&
                                        (isArtworkService
                                          ? !selection.artwork_service_description ||
                                            !selection.artwork_service_project_name
                                          : selection.artwork_service === 0)) ||
                                      (step === Steps.MATERIAL_AND_ADHESIVE && !paperSelected)
                                    }
                                    id={'calc_b_next' + step}
                                  >
                                    {calculatorInfos?.random_text?.next_button} <Icon name='arrow-right' />
                                  </Button>
                                )}
                                {(step === Steps.SUMMARY || !showAsWizard) && (
                                  <Button
                                    kind='warning'
                                    id={'calc_b_next_result' + step}
                                    onClick={handleStep}
                                    disabled={isLoading || !(data?.price?.priceTable?.length > 0)}
                                    isPill={true}
                                    style={{ width: '50%' }}
                                  >
                                    <Icon name='cart' />
                                    {calculatorInfos?.resume?.button.state_2}
                                  </Button>
                                )}
                              </S.GeneralWrap>
                            </Col>
                          </Row>
                        </S.BorderDivider>

                        <>
                          {calculatorInfos?.steps[`step_${step}`]['second_text'] && (
                            <S.BorderDivider>
                              <S.Paragraph
                                dangerouslySetInnerHTML={{
                                  __html: calculatorInfos?.steps[`step_${step}`]['second_text']
                                }}
                              />
                            </S.BorderDivider>
                          )}
                        </>
                      </>
                      {/*
                       *  HINT SECTION
                       */}
                      <>
                        {selection.sorts > 1 && finishingTypeHint && (
                          <S.BorderDivider>
                            <S.Paragraph
                              style={{
                                color: '#d62020',
                                fontSize: '0.85rem'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: calculatorInfos?.steps[`step_${step}`][finishingTypeHint]
                              }}
                            ></S.Paragraph>
                          </S.BorderDivider>
                        )}
                      </>
                      <>
                        {shapeName === 'custom' && (
                          <S.BorderDivider>
                            <S.Paragraph
                              style={{
                                color: '#d62020',
                                fontSize: '0.85rem'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: calculatorInfos?.steps[`step_${step}`]['custom_shape_hint']
                              }}
                            ></S.Paragraph>
                          </S.BorderDivider>
                        )}
                      </>

                      <>
                        {Number(selection.width) > 308 && (
                          <S.BorderDivider>
                            <S.Paragraph
                              style={{
                                color: '#d62020',
                                fontSize: '0.85rem'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: calculatorInfos?.steps[`step_${step}`]['greater_width_hint']
                              }}
                            ></S.Paragraph>
                          </S.BorderDivider>
                        )}
                      </>

                      <>
                        {finishingType === 'digital_foil' && (
                          <S.BorderDivider>
                            <S.Paragraph
                              style={{
                                color: '#d62020',
                                fontSize: '0.85rem'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: calculatorInfos?.steps[`step_${step}`]['digital_spot_varnish_hint']
                              }}
                            ></S.Paragraph>
                          </S.BorderDivider>
                        )}
                      </>

                      {/*
                       *  END OF HINT SECTION
                       */}
                    </S.Body>

                    {/*  Side box */}
                    {!isXlBreakpoint && data?.price && (
                      <>
                        {serviceType && serviceType !== defaultLocale ? (
                          <ServiceResult
                            proofing_service_model={calculatorInfos.proofing_service_model}
                            type={serviceType || process.env.NEXT_PUBLIC_DEFAULT_LOCALE}
                            price={data?.price?.total}
                          />
                        ) : (
                          <ResultFooter
                            texts={{
                              button: calculatorInfos?.resume?.button,
                              extra: calculatorInfos?.resume?.extra,
                              help: calculatorInfos?.resume?.help,
                              more_info: calculatorInfos?.resume?.more_info,
                              footer_message: calculatorInfos.resume?.footer_message
                            }}
                          />
                        )}
                      </>
                    )}
                  </>
                </Box>
              </Col>
              {/*  Side box */}

              {isXlBreakpoint && data?.price && (
                <Col xs={12} xl={4}>
                  {serviceType && serviceType !== defaultLocale ? (
                    <ServiceResult
                      proofing_service_model={calculatorInfos.proofing_service_model}
                      type={serviceType || process.env.NEXT_PUBLIC_DEFAULT_LOCALE}
                      price={data?.price?.total}
                    />
                  ) : (
                    <Result
                      result={data.price}
                      texts={calculatorInfos.resume}
                      error_messages={calculatorInfos?.error_messages}
                      compactView={compactView}
                    />
                  )}
                </Col>
              )}
            </>
          )}
        </Row>

        {isAlertOpen && calculatorInfos && (
          <Alert
            size='large'
            cancelButtonName={calculatorInfos.purchase_confirmation_modal.cancel_button_label}
            confirmButtonName={calculatorInfos.purchase_confirmation_modal.confirmation_button_label}
            close={() => routerPush(ROUTER_PATH.CART[defaultLocale])}
            action={() => {
              setIsAlertOpen(false)
              onContinueShopping?.() // Call a upper component function
            }}
          >
            <Image src='/icons/cart-checked.svg' alt='Cart checked' width='80' height='78' />
            <Text size='xxlg' family='heading' weight='bold' align='center' margin='2rem 0'>
              {calculatorInfos.purchase_confirmation_modal.title}
            </Text>
          </Alert>
        )}
      </div>
    </>
  )
}
